<template>
  <section>
    <b-modal
      id="digital-deposit-modal"
      ref="digital-deposit-modal"
      size="xl"
      :title="$t('message.digitalDeposit.title')"
      hide-footer
      no-close-on-backdrop
      @hide="hide()">
      <h6>{{ $t('message.digitalDeposit.description') }}</h6>
      <div class="row">
        <!--    Provider Data    -->
        <div class="col-12 mt-2">
          <div class="row">
            <div class="col-6">
              <label>
                {{ $t('message.digitalDeposit.chooseProvider') }}
                <coozzy-info-circle-icon
                  v-if="showGoCautionOption && isGoCautionDisabledByType"
                  v-b-tooltip.hover="{ title: $t('message.digitalDeposit.goCautionResidentialTooltip') }"
                  class="ml-1" />
              </label>
              <coozzy-form-select
                id="provider"
                v-model="provider"
                :state="$v.provider.$dirty && $v.provider.$error ? false : null">
                <option :value="''">
                  -
                </option>
                <option
                  v-if="isEvorestEnabled"
                  value="EVOREST">
                  {{
                    allowSwissCaution
                      ? $t('message.digitalDeposit.providers.EVOREST_WITH_SWISS_CAUTION')
                      : $t('message.digitalDeposit.providers.EVOREST')
                  }}
                </option>
                <option
                  v-if="showGoCautionOption"
                  value="GO_CAUTION"
                  :disabled="isGoCautionDisabledByType">
                  {{ $t('message.digitalDeposit.providers.GO_CAUTION') }}
                </option>
                <option
                  v-if="isFirstcautionEnabled"
                  value="FIRST_CAUTION">
                  {{ $t('message.digitalDeposit.providers.FIRST_CAUTION') }}
                </option>
              </coozzy-form-select>
            </div>
            <div
              v-if="provider === 'EVOREST'"
              class="col-12">
              <p class="mt-2">
                {{
                  allowSwissCaution
                    ? $t('message.digitalDeposit.descriptionProviderWithSwissCaution')
                    : $t('message.digitalDeposit.descriptionProvider')
                }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="providerSelected"
        class="row">
        <!--    Tenants Data    -->
        <div class="col-12">
          <div class="row">
            <template
              v-for="(contact, index) in listContacts">
              <div
                :key="contact.id + '_' + index"
                class="col-4 col-md-3">
                <div>
                  <template v-if="checkValidContact(contact)">
                    <coozzy-success-icon />
                    {{ $t('message.digitalDeposit.informationComplete') }}
                  </template>
                  <template v-else>
                    <coozzy-fail-icon />
                    {{ $t('message.digitalDeposit.informationIncomplete') }}
                    <coozzy-info-circle-icon
                      v-b-tooltip.hover.html="{ title: $t('message.digitalDeposit.informationIncompleteTooltip', { list: '<ul>' + listMissingFields(contact) + '</ul>' }), customClass: 'information-incomplete-tooltip' }"
                      class="mt-2" />
                  </template>
                </div>
                <div class="row">
                  <router-link
                    class="col"
                    :to="{ name: moduleRoutePrefix + 'ContactDetailsView', params: { id: contact.id } }">
                    {{ contact.name }}
                  </router-link>
                  <coozzy-button
                    design="transparent"
                    class="p-0 mb-0 float-right ml-2"
                    @click="editContactClicked(contact)">
                    <coozzy-edit-icon />
                  </coozzy-button>
                </div>
                <address-text :address="contact.address" />
                <div
                  v-if="'contactPersons' in contact"
                  class="row mt-2">
                  <label
                    for="contactPerson"
                    class="col-12">
                    {{ $t('message.digitalDeposit.selectContactPerson') + '*' }}
                    <coozzy-info-circle-icon
                      v-b-tooltip.hover="{ title: getContactPersonTooltipTitle(contact) }"
                      class="ml-1" />
                  </label>
                  <div class="col-12">
                    <coozzy-form-select
                      id="contactPerson"
                      :ref="'contactPerson_' + contact.id"
                      v-model="selectedContactPersonId"
                      :state="selectedContactPersonId === '' && $v.listContacts && $v.listContacts.$dirty && $v.listContacts.$error ? false : null"
                      @change="contactPersonSelected(contact)">
                      <option :value="''">
                        -
                      </option>
                      <option
                        v-for="contactPerson of getActiveContactPersons(contact)"
                        :key="contactPerson.id"
                        :value="contactPerson.id">
                        {{ contactPerson.name }}
                      </option>
                    </coozzy-form-select>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <!--    Provider insurance (read-only)    -->
        <div
          v-if="showProviderInsuranceRow"
          class="col-12 mt-2">
          <div class="row">
            <div class="col-6">
              <label>
                {{ $t('message.digitalDeposit.insuranceName') }}
              </label>
              <coozzy-form-input
                :value="providerInsuranceName"
                :disabled="true"
                :placeholder="$t('message.digitalDeposit.insuranceName')" />
            </div>
          </div>
        </div>
        <!--    Deposit Data    -->
        <div class="col-12 mt-2">
          <div class="row">
            <div class="col-6">
              <label>
                {{ $t('message.onBoarding.buildings.objects.tenancies.deposit.amount') + '*' }}
              </label>
              <coozzy-form-input-numeric
                v-model="depositAmount"
                :filter="'formatPrice'"
                type="number"
                :min="0"
                placeholder="CHF"
                :is-decimal="true"
                :check-valide="$v.depositAmount.$dirty && $v.depositAmount.$error ? 'is-invalid' : ''" />
            </div>
            <div class="col-6">
              <label>
                {{ $t('message.onBoarding.buildings.objects.tenancies.deposit.dueDate') + '*' }}
              </label>
              <coozzy-form-input
                id="dueDate"
                v-model="dates.depositDueDate"
                :filter="'formatDate'"
                type="date"
                :state="($v.depositDueDate.$dirty && $v.depositDueDate.$error) ? false : null"
                @blur="dateToObject()" />
            </div>
          </div>
        </div>
      </div>
      <template v-if="provider === 'EVOREST'">
        <h5 class="mt-3">
          {{ $t('message.digitalDeposit.investment') }}
        </h5>
        <h6>{{ $t('message.digitalDeposit.descriptionInvestment') }}</h6>
        <div class="row mt-2">
          <div class="col-3">
            <coozzy-form-radio
              v-model="allowInvestment"
              :default-model="allowInvestment"
              :select-value="true"
              class=""
              :disabled="ownerAllowInvestment === 'DOES_NOT_APPLY'"
              name="allowInvestment">
              {{ $t('message.digitalDeposit.options.allowInvestment') }}
            </coozzy-form-radio>
          </div>
          <div class="col-3">
            <coozzy-form-radio
              v-model="allowInvestment"
              :default-model="allowInvestment"
              :select-value="false"
              class=""
              name="allowInvestment">
              {{ $t('message.digitalDeposit.options.notAllowInvestment') }}
            </coozzy-form-radio>
          </div>
        </div>
      </template>
      <div class="col mt-3 p-0">
        <coozzy-button
          size="small"
          class="border mb-0"
          design="transparent"
          @click="hide">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          design="green"
          @click="saveDeposit">
          {{ $t('message.generic.form.send') }}
        </coozzy-button>
      </div>
    </b-modal>
    <create-contact-modal
      v-if="contactToEdit"
      :key="contactToEdit ? contactToEdit.id : null"
      ref="create-contact-modal"
      :show-search="false"
      :show-request-sources="false"
      :set-interested-party-role="false"
      :show-add-another="true"
      :from-digital-deposit="true"
      :contact-to-edit="contactToEdit"
      @contact-updated="contactUpdated"
      @modal-hide="createContactModalHide" />
  </section>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import CoozzySuccessIcon from '@/framework/components/icons/CoozzySuccessIcon.vue'
import CoozzyFailIcon from '@/framework/components/icons/CoozzyFailIcon.vue'
import { routeChecks } from '@/mixins/routeChecks'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon.vue'
import AddressText from '@/framework/components/misc/AddressText.vue'
import CreateContactModal from '@/contact/components/CreateContactModal.vue'
import {
  ApplicableApplicable, Contactcontactv2Company,
  ObjectobjectObject,
  ObjecttenancyTenancy,
  TypeDate,
  V2Person
} from '@/types/api'
import { isEvorestUidRequiredLegalForm, isUidFilledForEvorest } from '@/mixins/validation'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import { minValue, required } from 'vuelidate/lib/validators'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyFormInputNumeric from '@/framework/components/form/input/CoozzyFormInputNumeric.vue'
import { dateUtils } from '@/mixins/dateUtils'
import CoozzyFormRadio from '@/framework/components/form/radio/CoozzyFormRadio.vue'
import EvorestApi from '@/misc/apis/EvorestApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import ContactApi from '@/misc/apis/ContactApi'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon.vue'
import GocautionApi from '@/misc/apis/GocautionApi'
import FirstcautionApi from '@/misc/apis/FirstcautionApi'

type Contact = V2Person | Contactcontactv2Company

export default Vue.extend({
  name: 'DigitalDepositModal',
  components: {
    CoozzyInfoCircleIcon,
    CoozzyFormRadio,
    CoozzyFormInputNumeric,
    CoozzyFormInput,
    CoozzyFormSelect,
    CreateContactModal,
    AddressText,
    CoozzyEditIcon,
    CoozzyButton,
    CoozzyFailIcon,
    CoozzySuccessIcon
  },
  mixins: [routeChecks, dateUtils],
  props: {
    tenancy: {
      type: Object as PropType<ObjecttenancyTenancy>,
      required: true
    },
    object: {
      type: Object as PropType<ObjectobjectObject>,
      required: true
    },
    contacts: {
      type: Array as PropType<Contact[]>,
      required: true
    },
    accountId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false as boolean,
      listContacts: [] as Contact[],
      contactToEdit: null as Contact | null,
      provider: '' as string,
      depositAmount: null as number,
      dates: {
        depositDueDate: '' as string
      },
      depositDueDate: null as TypeDate,
      allowInvestment: true as boolean,
      ownerAllowInvestment: 'UNDEFINED_VALUE' as ApplicableApplicable,
      selectedContactPersonId: '' as string,
      isEvorestEnabled: false as boolean,
      isGoCautionEnabled: false as boolean,
      isFirstcautionEnabled: false as boolean,
      allowSwissCautionEnabled: false as boolean
    }
  },
  computed: {
    allowSwissCaution(): boolean {
      return this.allowSwissCautionEnabled
    },
    showProviderInsuranceRow(): boolean {
      return this.provider === 'GO_CAUTION' || this.provider === 'FIRST_CAUTION'
    },
    providerInsuranceName(): string {
      if (this.provider === 'FIRST_CAUTION') {
        return 'FirstCaution'
      }
      if (this.provider === 'GO_CAUTION') {
        return 'goCaution'
      }
      return ''
    },
    providerSelected() {
      return this.provider !== ''
    },
    showGoCautionOption() {
      return this.isGoCautionEnabled && !this.listContacts.some((contact: Contact) => this.isCompanyContact(contact))
    },
    isGoCautionDisabledByType() {
      return this.showGoCautionOption && this.tenancy?.contract?.typeOfUse !== 'TYPE_OF_USE_RESIDENTIAL'
    }
  },
  watch: {
    contacts: {
      handler() {
        this.listContacts = this.contacts
      },
      deep: true
    },
    showGoCautionOption(value) {
      if (!value && this.provider === 'GO_CAUTION') {
        this.provider = ''
      }
    },
    isGoCautionDisabledByType(value) {
      if (value && this.provider === 'GO_CAUTION') {
        this.provider = ''
      }
    }
  },
  mounted() {
    this.listContacts = this.contacts
    this.getEvorestSettings()
    this.getProviderStatus()
    if (this.tenancy?.deposit) {
      this.depositAmount = this.tenancy.deposit.amount !== -1 ? this.tenancy.deposit.amount : null
      this.depositDueDate = this.tenancy.deposit.dueDate
      this.dates.depositDueDate = this.$options.filters.objectToDateInput(this.tenancy.deposit.dueDate)
    }
  },
  methods: {
    isCompanyContact(contact: Contact) {
      return 'legalForm' in contact || 'uniqueEnterpriseIdentification' in contact
    },
    isCompanyUidFieldFilled(uid: string | null | undefined) {
      return isUidFilledForEvorest(uid)
    },
    hasAnyValidPhone(contact: Contact) {
      return (contact.phoneNumbers || []).some(phone => {
        const type = typeof phone.type === 'string' ? phone.type.toUpperCase() : ''
        const number = typeof phone.number === 'string' ? phone.number.trim() : ''
        return ['MOBILE', 'WORK', 'HOME'].includes(type) && number !== ''
      })
    },
    getProviderStatus() {
      Promise.all([
        EvorestApi.hasEvorestEnabled(this.accountId).catch(() => ({ enabled: false })),
        GocautionApi.hasEnabled(this.accountId).catch(() => ({ enabled: false })),
        FirstcautionApi.hasEnabled(this.accountId).catch(() => ({ enabled: false }))
      ]).then(([evorestStatus, goCautionStatus, firstcautionStatus]) => {
        this.isEvorestEnabled = !!evorestStatus?.enabled
        this.allowSwissCautionEnabled = !!(('allowSwissCaution' in evorestStatus) && evorestStatus.allowSwissCaution)
        this.isGoCautionEnabled = !!goCautionStatus?.enabled
        this.isFirstcautionEnabled = !!firstcautionStatus?.enabled
      })
    },
    formatContactPersonDateOfBirth(dateOfBirth: TypeDate | null | undefined): string {
      if (!dateOfBirth || dateOfBirth.year === undefined || dateOfBirth.year === 0) {
        return '-'
      }
      const m = (dateOfBirth.month ?? 0).toString().padStart(2, '0')
      const d = (dateOfBirth.day ?? 0).toString().padStart(2, '0')
      return `${dateOfBirth.year}-${m}-${d}`
    },
    formatContactPersonCivilStatus(civilStatus: string | null | undefined): string {
      if (!civilStatus || civilStatus === 'UNDEFINED_CIVIL_STATUS') {
        return '-'
      }
      const keyMap: Record<string, string> = {
        CIVIL_STATUS_SINGLE: 'message.applicationForm.civilStatus.single',
        CIVIL_STATUS_MARRIED: 'message.applicationForm.civilStatus.married',
        CIVIL_STATUS_DIVORCED: 'message.applicationForm.civilStatus.divorced',
        CIVIL_STATUS_WIDOWED: 'message.applicationForm.civilStatus.widowed',
        CIVIL_STATUS_SEPARATED: 'message.applicationForm.civilStatus.separated',
        CIVIL_STATUS_REGISTERED_PARTNERSHIP: 'message.applicationForm.civilStatus.registeredPartnership',
        CIVIL_STATUS_DISSOLVED_PARTNERSHIP: 'message.applicationForm.civilStatus.dissolvedPartnership'
      }
      const key = keyMap[civilStatus]
      return key ? this.$t(key) : '-'
    },
    getContactPersonTooltipContent(contactPerson: {
      salutationName?: string | null
      dateOfBirth?: TypeDate | null
      civilStatus?: string | null
    } | null | undefined): string {
      if (!contactPerson) {
        return ''
      }
      const salutation = (contactPerson.salutationName && contactPerson.salutationName.trim() !== '')
        ? contactPerson.salutationName
        : '-'
      const dateOfBirth = this.formatContactPersonDateOfBirth(contactPerson.dateOfBirth ?? null)
      const civilStatus = this.formatContactPersonCivilStatus(contactPerson.civilStatus ?? null)
      return `${this.$t('message.generic.salutation')}: ${salutation}\n${this.$t('message.generic.birthday')}: ${dateOfBirth}\n${this.$t('message.applicationForm.civilStatus.title')}: ${civilStatus}`
    },
    getContactPersonTooltipTitle(contact: Contact): string {
      const activeContactPersons = this.getActiveContactPersons(contact)
      if (activeContactPersons.length === 0) {
        return this.$t('message.digitalDeposit.contactPersonTooltipPlaceholder')
      }
      const selected = activeContactPersons.find((p: { id: string }) => p.id === this.selectedContactPersonId)
      if (!selected) {
        return this.$t('message.digitalDeposit.contactPersonTooltipPlaceholder')
      }
      return this.getContactPersonTooltipContent(selected)
    },
    getActiveContactPersons(contact: Contact) {
      if (!('contactPersons' in contact) || !contact.contactPersons?.length) {
        return []
      }
      return contact.contactPersons.filter((contactPerson) => contactPerson.active !== false)
    },
    contactPersonSelected(contact: Contact) {
      console.log('contactPersonSelected', contact)
      // if (contact.selectedContactPerson) {
      //   const contactPerson = contact.contactPersons.find((cp) => cp.id === contact.selectedContactPerson)
      //   if (contactPerson) {
      //     contact.firstName = contactPerson.firstName
      //     contact.lastName = contactPerson.lastName
      //     contact.email = contactPerson.email
      //     contact.phoneNumbers = contactPerson.phoneNumbers
      //     contact.address = contactPerson.address
      //   }
      // } else {
      //   // Resetting the selected person
      //   contact.firstName = ''
      //   contact.lastName = ''
      //   contact.email = ''
      //   contact.phoneNumbers = []
      //   contact.address = { country: '', street: '', streetNumber: '', city: '', zip: '' }
      // }
    },
    getEvorestSettings() {
      EvorestApi.getOwnerConfig(this.object.ownerId).then((response) => {
        this.ownerAllowInvestment = response.allowInvestment
        if (this.ownerAllowInvestment === 'DOES_NOT_APPLY') {
          this.allowInvestment = false
        }
      })
    },
    hide() {
      this.$refs['digital-deposit-modal']?.hide()
      this.$emit('hide')
    },
    async refreshContacts() {
      const contactIds = this.tenancy?.tenant?.contactIds || []
      if (!contactIds.length) {
        this.listContacts = this.contacts
        return
      }
      try {
        const response = await ContactApi.contactResolve(contactIds)
        this.listContacts = (response.persons || [] as Contact[]).concat(response.companies || [])
        this.listContacts.forEach((contact: Contact) => {
          this.$emit('contact-updated', { contact })
        })
      } catch (e) {
        console.log(e)
        this.listContacts = this.contacts
      }
    },
    async show() {
      await this.refreshContacts()
      this.$refs['digital-deposit-modal']?.show()
    },
    checkValidContact(contact: Contact) {
      if (this.provider === 'GO_CAUTION') {
        if (this.isCompanyContact(contact)) {
          return false
        }
        const personContact = contact as V2Person
        const hasRequiredFields = [
          'firstName',
          'lastName',
          'email',
          'dateOfBirth'
        ].every(field => personContact[field] !== '' && personContact[field] !== null)
        const hasAddress = personContact.address.street !== '' &&
          personContact.address.streetNumber !== '' &&
          personContact.address.zip !== '' &&
          personContact.address.city !== ''
        const hasPreferredLanguage = personContact.preferredLanguage && personContact.preferredLanguage !== 'PREFERRED_LANGUAGE_UNDEFINED'
        const hasSalutation = personContact.gender !== 'UNDEFINED_GENDER'
        const hasCivilStatus = personContact.civilStatus !== 'UNDEFINED_CIVIL_STATUS'
        const hasNationality = personContact.nationality && personContact.nationality !== ''
        const hasPhone = this.hasAnyValidPhone(personContact)
        return hasRequiredFields && hasAddress && hasPreferredLanguage && hasSalutation && hasCivilStatus && hasNationality && hasPhone
      }
      if (this.provider === 'FIRST_CAUTION') {
        if (this.isCompanyContact(contact)) {
          const companyContact = contact as Contactcontactv2Company
          const hasRequiredFields = [
            'name',
            'email'
          ].every(field => companyContact[field] !== '' && companyContact[field] !== null)
          const hasAddress = companyContact.address.street !== '' &&
            companyContact.address.streetNumber !== '' &&
            companyContact.address.zip !== '' &&
            companyContact.address.city !== ''
          const hasLegalForm = companyContact.legalForm !== 'LEGAL_FORM_UNDEFINED'
          const hasPhone = this.hasAnyValidPhone(companyContact)
          return hasRequiredFields && hasAddress && hasLegalForm && hasPhone
        }
        const personContact = contact as V2Person
        const hasRequiredFields = [
          'firstName',
          'lastName',
          'email'
        ].every(field => personContact[field] !== '' && personContact[field] !== null)
        const hasDateOfBirth = !!personContact.dateOfBirth &&
          personContact.dateOfBirth.year !== undefined &&
          personContact.dateOfBirth.year !== 0
        const hasAddress = personContact.address.street !== '' &&
          personContact.address.streetNumber !== '' &&
          personContact.address.zip !== '' &&
          personContact.address.city !== ''
        const hasSalutation = personContact.gender !== 'UNDEFINED_GENDER'
        const hasCivilStatus = personContact.civilStatus !== 'UNDEFINED_CIVIL_STATUS'
        const hasNationality = personContact.nationality && personContact.nationality !== ''
        const hasPhone = this.hasAnyValidPhone(personContact)
        return hasRequiredFields && hasDateOfBirth && hasAddress && hasSalutation && hasCivilStatus && hasNationality && hasPhone
      }
      let contactValid = false
      // const contactToCheck = isCompany ? contact instanceof Contactcontactv2Company : contact instanceof V2Person
      if (this.isCompanyContact(contact)) {
        const companyContact = contact as Contactcontactv2Company
        const uidRequiredForEvorest = this.provider !== 'EVOREST' ||
          isEvorestUidRequiredLegalForm(companyContact.legalForm)
        const requiredFieldsCompany = [
          'name',
          'email'
        ]
        const areRequiredFieldsFilled = requiredFieldsCompany.every(field => companyContact[field] !== '' && companyContact[field] !== null) &&
          (!uidRequiredForEvorest || this.isCompanyUidFieldFilled(companyContact.uniqueEnterpriseIdentification))
        const isLegalFormFilled = companyContact.legalForm !== 'LEGAL_FORM_UNDEFINED'
        const countryFilled = companyContact.address.country !== '' && companyContact.address.country !== null
        contactValid = areRequiredFieldsFilled && isLegalFormFilled && countryFilled
      } else {
        const personContact = contact as V2Person
        const requiredFields = [
          'firstName',
          'lastName',
          'email',
          'nationality',
          'dateOfBirth',
          'preferredLanguage'
        ]

        const areRequiredFieldsFilled = requiredFields.every(field => personContact[field] !== '' && personContact[field] !== null)
        const hasPreferredLanguage = personContact.preferredLanguage && personContact.preferredLanguage !== 'PREFERRED_LANGUAGE_UNDEFINED'
        const isGenderFilled = personContact.gender !== 'UNDEFINED_GENDER'
        const isCivilStatusFilled = personContact.civilStatus !== 'UNDEFINED_CIVIL_STATUS'
        const notForeignCitizenship = personContact.nationality !== 'FOREIGN_CITIZENSHIP'
        const streetFilled = personContact.address.street !== '' && personContact.address.street !== null
        const streetNumberFilled = personContact.address.streetNumber !== '' && personContact.address.streetNumber !== null

        contactValid = areRequiredFieldsFilled && hasPreferredLanguage && isGenderFilled && isCivilStatusFilled && notForeignCitizenship && streetFilled && streetNumberFilled
      }

      const isAtLeastOnePhoneFilled = this.hasAnyValidPhone(contact)

      return isAtLeastOnePhoneFilled && contactValid
    },
    listMissingFields(contact: Contact) {
      let missingFields = [] as string[]
      if (this.provider === 'GO_CAUTION') {
        if (this.isCompanyContact(contact)) {
          return ''
        }
        const personContact = contact as V2Person
        if (personContact.firstName === '' || personContact.firstName === null) {
          missingFields.push('firstName')
        }
        if (personContact.lastName === '' || personContact.lastName === null) {
          missingFields.push('lastName')
        }
        if (!personContact.email || personContact.email === '') {
          missingFields.push('email')
        }
        if (!personContact.dateOfBirth || personContact.dateOfBirth.year === undefined || personContact.dateOfBirth.year === 0) {
          missingFields.push('dateOfBirth')
        }
        if (personContact.address.street === '' || personContact.address.street === null) {
          missingFields.push('street')
        }
        if (personContact.address.streetNumber === '' || personContact.address.streetNumber === null) {
          missingFields.push('streetNumber')
        }
        if (personContact.address.zip === '' || personContact.address.zip === null) {
          missingFields.push('zip')
        }
        if (personContact.address.city === '' || personContact.address.city === null) {
          missingFields.push('city')
        }
        if (!personContact.preferredLanguage || personContact.preferredLanguage === 'PREFERRED_LANGUAGE_UNDEFINED') {
          missingFields.push('preferredLanguage')
        }
        if (!personContact.gender || personContact.gender === 'UNDEFINED_GENDER') {
          missingFields.push('gender')
        }
        if (!personContact.civilStatus || personContact.civilStatus === 'UNDEFINED_CIVIL_STATUS') {
          missingFields.push('civilStatus')
        }
        if (personContact.nationality === '' || personContact.nationality === null) {
          missingFields.push('nationality')
        }
        if (!this.hasAnyValidPhone(personContact)) {
          missingFields.push('phoneNumbers')
        }
      } else if (this.provider === 'FIRST_CAUTION') {
        if (this.isCompanyContact(contact)) {
          const companyContact = contact as Contactcontactv2Company
          if (companyContact.name === '' || companyContact.name === null) {
            missingFields.push('name')
          }
          if (!companyContact.email || companyContact.email === '') {
            missingFields.push('email')
          }
          if (companyContact.address.street === '' || companyContact.address.street === null) {
            missingFields.push('street')
          }
          if (companyContact.address.streetNumber === '' || companyContact.address.streetNumber === null) {
            missingFields.push('streetNumber')
          }
          if (companyContact.address.zip === '' || companyContact.address.zip === null) {
            missingFields.push('zip')
          }
          if (companyContact.address.city === '' || companyContact.address.city === null) {
            missingFields.push('city')
          }
          if (companyContact.legalForm === 'LEGAL_FORM_UNDEFINED') {
            missingFields.push('legalForm')
          }
          if (!this.hasAnyValidPhone(companyContact)) {
            missingFields.push('phoneNumbers')
          }
        } else {
          const personContact = contact as V2Person
          if (personContact.firstName === '' || personContact.firstName === null) {
            missingFields.push('firstName')
          }
          if (personContact.lastName === '' || personContact.lastName === null) {
            missingFields.push('lastName')
          }
          if (!personContact.email || personContact.email === '') {
            missingFields.push('email')
          }
          if (!personContact.dateOfBirth || personContact.dateOfBirth.year === undefined || personContact.dateOfBirth.year === 0) {
            missingFields.push('dateOfBirth')
          }
          if (personContact.address.street === '' || personContact.address.street === null) {
            missingFields.push('street')
          }
          if (personContact.address.streetNumber === '' || personContact.address.streetNumber === null) {
            missingFields.push('streetNumber')
          }
          if (personContact.address.zip === '' || personContact.address.zip === null) {
            missingFields.push('zip')
          }
          if (personContact.address.city === '' || personContact.address.city === null) {
            missingFields.push('city')
          }
          if (!personContact.gender || personContact.gender === 'UNDEFINED_GENDER') {
            missingFields.push('gender')
          }
          if (!personContact.civilStatus || personContact.civilStatus === 'UNDEFINED_CIVIL_STATUS') {
            missingFields.push('civilStatus')
          }
          if (personContact.nationality === '' || personContact.nationality === null) {
            missingFields.push('nationality')
          }
          if (!this.hasAnyValidPhone(personContact)) {
            missingFields.push('phoneNumbers')
          }
        }
      } else {
      // const contactToCheck = isCompany ? contact instanceof Contactcontactv2Company : contact instanceof V2Person
        if (this.isCompanyContact(contact)) {
        const companyContact = contact as Contactcontactv2Company
        const uidRequiredForEvorest = this.provider !== 'EVOREST' ||
          isEvorestUidRequiredLegalForm(companyContact.legalForm)
        const requiredFieldsCompany = [
          'name',
          'email'
        ]
        missingFields = requiredFieldsCompany.filter(field => companyContact[field] === '' || companyContact[field] === null)
        if (uidRequiredForEvorest && !this.isCompanyUidFieldFilled(companyContact.uniqueEnterpriseIdentification)) {
          missingFields.push('uniqueEnterpriseIdentification')
        }
        if (companyContact.legalForm === 'LEGAL_FORM_UNDEFINED') {
          missingFields.push('legalForm')
        }
        if (companyContact.address.country === '' || companyContact.address.country === null) {
          missingFields.push('country')
        }
        } else {
        const personContact = contact as V2Person
        const requiredFields = [
          'firstName',
          'lastName',
          'email',
          'nationality'
        ]

        missingFields = requiredFields.filter(field => personContact[field] === '' || personContact[field] === null || personContact[field] === undefined)
        if (!personContact.dateOfBirth || personContact.dateOfBirth.year === undefined || personContact.dateOfBirth.year === 0) {
          missingFields.push('dateOfBirth')
        }
        if (!personContact.preferredLanguage || personContact.preferredLanguage === 'PREFERRED_LANGUAGE_UNDEFINED') {
          missingFields.push('preferredLanguage')
        }

        if (!personContact.gender || personContact.gender === 'UNDEFINED_GENDER') {
          missingFields.push('gender')
        }

        if (!personContact.civilStatus || personContact.civilStatus === 'UNDEFINED_CIVIL_STATUS') {
          missingFields.push('civilStatus')
        }
        if (personContact.nationality === 'FOREIGN_CITIZENSHIP') {
          missingFields.push('nationality')
        }
        if (personContact.address.street === '' || personContact.address.street === null) {
          missingFields.push('street')
        }
        if (personContact.address.streetNumber === '' || personContact.address.streetNumber === null) {
          missingFields.push('streetNumber')
        }
        }
        if (!this.hasAnyValidPhone(contact)) {
          missingFields.push('phoneNumbers')
        }
      }
      let htmlList = ''
      missingFields.forEach((field) => {
        switch (field) {
          case 'firstName':
            htmlList += `<li>${this.$t('message.generic.firstName')}</li>`
            break
          case 'lastName':
            htmlList += `<li>${this.$t('message.generic.lastName')}</li>`
            break
          case 'email':
            htmlList += `<li>${this.$t('message.generic.email')}</li>`
            break
          case 'nationality':
            htmlList += `<li>${this.$t('message.generic.nationality.title')}</li>`
            break
          case 'dateOfBirth':
            htmlList += `<li>${this.$t('message.generic.birthday')}</li>`
            break
          case 'gender':
            htmlList += `<li>${this.$t('message.generic.title.title')}</li>`
            break
          case 'civilStatus':
            htmlList += `<li>${this.$t('message.applicationForm.civilStatus.title')}</li>`
            break
          case 'phoneNumbers':
            htmlList += `<li>${this.$t('message.generic.phone.mobile')}</li>`
            break
          case 'street':
            htmlList += `<li>${this.$t('message.generic.street')}</li>`
            break
          case 'streetNumber':
            htmlList += `<li>${this.$t('message.generic.no')}</li>`
            break
          case 'name':
            htmlList += `<li>${this.$t('message.generic.companyName')}</li>`
            break
          case 'uniqueEnterpriseIdentification':
            htmlList += `<li>${this.$t('message.generic.uid')}</li>`
            break
          case 'legalForm':
            htmlList += `<li>${this.$t('message.contact.legalForm')}</li>`
            break
          case 'country':
            htmlList += `<li>${this.$t('message.generic.nationality.title')}</li>`
            break
          case 'zip':
            htmlList += `<li>${this.$t('message.generic.zip')}</li>`
            break
          case 'city':
            htmlList += `<li>${this.$t('message.generic.city')}</li>`
            break
          case 'preferredLanguage':
            htmlList += `<li>${this.$t('message.contact.preferredLanguage')}</li>`
            break
        }
      })
      return htmlList
    },
    editContactClicked(contact: Contact) {
      this.contactToEdit = contact
      this.$nextTick(function () {
        this.$refs['create-contact-modal'].show()
      })
    },
    createContactModalHide() {
      this.$refs['create-contact-modal'].hide()
      this.contactToEdit = null
    },
    isSameContact(existing: Contact, contact: Contact) {
      if (!existing || !contact) {
        return false
      }
      // Contacts may be keyed by id or objectID (tenant contactIds often store objectID).
      const existingAny = existing as Contact & { objectID?: string }
      const contactAny = contact as Contact & { objectID?: string }
      return existingAny.id === contactAny.id ||
        existingAny.objectID === contactAny.id ||
        (!!contactAny.objectID && (existingAny.id === contactAny.objectID || existingAny.objectID === contactAny.objectID))
    },
    contactUpdated(data: { contact: Contact }) {
      const contact = data.contact
      const index = this.listContacts.findIndex((c: Contact) => this.isSameContact(c, contact))
      if (index !== -1) {
        this.$set(this.listContacts, index, contact)
      }
      this.$emit('contact-updated', data)
    },
    dateToObject() {
      this.depositDueDate = this.toObject(this.dates.depositDueDate)
    },
    hasRussianNationalityForEvorest() {
      return this.listContacts.some((contact: Contact) => {
        if (this.isCompanyContact(contact)) {
          const companyContact = contact as Contactcontactv2Company
          const contactPerson = this.getActiveContactPersons(companyContact)
            .find((person: { id: string; nationality?: string }) => person.id === this.selectedContactPersonId)
          return contactPerson?.nationality === 'RU'
        }
        return (contact as V2Person).nationality === 'RU'
      })
    },
    saveDeposit() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        if (this.$v.listContacts.$dirty && this.$v.listContacts.$error) {
          Vue.toasted.show(this.$t('message.savingErrors.digitalDepositValidContacts'), { type: 'error' })
        }
        return
      }
      if (this.provider === 'EVOREST' && this.hasRussianNationalityForEvorest()) {
        Vue.toasted.show(this.$t('message.savingErrors.digitalDepositRussianNationality'), { type: 'error' })
        return
      }
      const bankAddress = { country: 'CH', state: '', city: 'Lenzburg 1', zip: '5600', street: 'Bahnhofstrasse 2', streetNumber: '', streetAddition: '', subUnit: '', careOf: '', geo: null, mailbox: '' }
      const tenancy = this.tenancy
      if (this.provider === 'GO_CAUTION') {
        tenancy.deposit.type = 'DEPOSIT_TYPE_INSURANCE_GUARANTEE'
        tenancy.deposit.insuranceCompany = 'goCaution'
      } else if (this.provider === 'FIRST_CAUTION') {
        tenancy.deposit.type = 'DEPOSIT_TYPE_INSURANCE_GUARANTEE'
        tenancy.deposit.insuranceCompany = 'FirstCaution'
      } else {
        tenancy.deposit.bankName = 'Hypothekarbank Lenzburg AG'
        tenancy.deposit.bic = 'HYPLCH22XXX'
        tenancy.deposit.clearingNumber = 8307
        tenancy.deposit.type = 'DEPOSIT_TYPE_DEPOT'
        tenancy.deposit.address = bankAddress
      }
      tenancy.deposit.amount = this.depositAmount
      tenancy.deposit.dueDate = this.depositDueDate
      ObjectApi.updateTenancy(tenancy)
        .then(() => {
          this.$store.dispatch('onboarding/updateSavedTenancy', tenancy)
          let openDeposit
          if (this.provider === 'GO_CAUTION') {
            openDeposit = GocautionApi.openDeposit(this.object.id, this.tenancy.id, this.selectedContactPersonId)
          } else if (this.provider === 'FIRST_CAUTION') {
            openDeposit = FirstcautionApi.openDeposit(this.object.id, this.tenancy.id, this.selectedContactPersonId)
          } else {
            openDeposit = EvorestApi.openDeposit(this.object.id, this.tenancy.id, this.allowInvestment, this.selectedContactPersonId)
          }
          openDeposit.then(() => {
            this.hide()
            Vue.toasted.show(this.$t('message.successMessages.digitalDepositCreation'), { type: 'success' })
          }).catch((error) => {
            this.hide()
            const messageError = error?.response?.data?.message || this.$t('message.savingErrors.onboarding')
            Vue.toasted.show(messageError, { type: 'error' })
          })
        })
    }
  },
  validations() {
    return {
      listContacts: {
        required: (contacts) => {
          if (!this.provider) {
            return true
          }
          return contacts.every((contact: Contact) => this.checkValidContact(contact) && (
            this.provider === 'GO_CAUTION' ? true : (this.isCompanyContact(contact) ? this.selectedContactPersonId !== '' : true)
          ))
        }
      },
      provider: {
        required
      },
      depositAmount: {
        required,
        minValue: minValue(1)
      },
      depositDueDate: {
        required
      },
      allowInvestment: {
        required
      }
    }
  }
})
</script>

<style lang="scss" scoped>
:deep(#digital-deposit-modal .modal-xl) {
  max-width: 50% !important;
}
:deep(.information-incomplete-tooltip .tooltip-inner) {
  text-align: left;
  ul {
    padding-left: 10px;
  }
}
</style>
